import Heading2 from "components/Heading/Heading2";
import LoginModal from "components/LoginModal/LoginModal";
import ServiceCard from "components/ServiceCard/ServiceCard";
import SmartServiceFilters from "components/SmartServiceFilters/SmartServiceFilters";
import { Service } from "data/types";
import { useAppDispatch, useAppSelector, useScrollToTop } from "hooks/hooks";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { HiOutlineSquare3Stack3D, HiOutlineSparkles } from "react-icons/hi2";
import { useLocation, useNavigate } from "react-router-dom";
import { filterAllServices } from "redux/features/service/serviceSlice";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import Pagination from "shared/Pagination/Pagination";
import { SkeletonLoader } from "utils/SkeletonLoader";
import Header2 from "components/Header/Header2";
import { motion } from "framer-motion";
import { EASING } from "../../utils/easingUtils";

export interface SectionGridFilterCardProps {
  className?: string;
  data?: Service[];
  title?: string;
  selectedEvent?: string;
  region?: string;
}

const SectionGridFieldFilterCard: FC<SectionGridFilterCardProps> = ({ className = "" }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  const dispatch = useAppDispatch();
  const serviceState = useAppSelector((state) => state?.service?.allServices);
  const totalPages = useAppSelector((state) => state?.service?.totalPages);
  const location = useLocation();
  useScrollToTop();
  const searchParams = new URLSearchParams(location.search);
  const navigate = useNavigate();
  const handlePageChange = (pageNumber: number) => {
    const params = new URLSearchParams(location.search);
    params.set("page", String(pageNumber));

    navigate(`${location.pathname}?${params.toString()}`);
    setCurrentPage(pageNumber);
  };

  const pathname = location.pathname;
  const decodedPathname = decodeURIComponent(pathname);
  const afterField = decodedPathname.split("/field/")[1];

  const fields = searchParams.get("field")?.toLowerCase();
  const region = searchParams.get("region")?.toLowerCase();

  useEffect(() => {
    const page = Number(searchParams.get("page")) || 1;
    const fieldParam = fields || afterField || "";
    const regionParam = region || "";

    const filter = {
      fields: fieldParam || undefined,
      region: regionParam || undefined,
      page,
    };

    setCurrentPage(page);
    dispatch(filterAllServices(filter)).then(() => setIsLoading(false));
  }, [location.search]);

  const handleApplyFilters = (filters: any) => {
    const params = new URLSearchParams();
    if (filters.field) params.set("field", filters.field.toLowerCase());
    if (filters.region) params.set("region", filters.region.toLowerCase());
    params.set("page", "1");

    navigate(`${location.pathname}?${params.toString()}`);
  };

  // Handle smart service filters changes
  const handleSmartServiceFiltersChange = (smartFilters: any) => {
    setIsLoading(true);

    // Transform smart filters to API format
    const apiFilters: any = {};

    if (smartFilters.search) apiFilters.search = smartFilters.search;
    if (smartFilters.location) apiFilters.region = smartFilters.location;
    if (smartFilters.minPrice) apiFilters.minPrice = smartFilters.minPrice;
    if (smartFilters.maxPrice) apiFilters.maxPrice = smartFilters.maxPrice;
    if (smartFilters.experience) apiFilters.experience = smartFilters.experience;
    if (smartFilters.specialties && smartFilters.specialties.length > 0) {
      apiFilters.specialties = smartFilters.specialties.join(",");
    }

    // Handle quick filters
    if (smartFilters.availableWeekend) apiFilters.availableWeekend = true;
    if (smartFilters.topRated) apiFilters.topRated = true;
    if (smartFilters.certified) apiFilters.certified = true;
    if (smartFilters.premium) apiFilters.premium = true;
    if (smartFilters.instantQuote) apiFilters.instantQuote = true;
    if (smartFilters.trending) apiFilters.trending = true;
    if (smartFilters.ecoFriendly) apiFilters.ecoFriendly = true;
    if (smartFilters.lastMinute) apiFilters.lastMinute = true;

    // Handle sorting
    if (smartFilters.sortBy) apiFilters.sortBy = smartFilters.sortBy;

    // Preserve existing URL filters
    if (fields && !smartFilters.search) apiFilters.fields = fields;
    if (region && !smartFilters.location) apiFilters.region = region;

    // Update URL
    const params = new URLSearchParams(location.search);
    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] && apiFilters[key] !== "") {
        params.set(key, apiFilters[key].toString());
      }
    });
    params.set("page", "1");
    navigate(`${location.pathname}?${params.toString()}`);

    // Call API with transformed filters
    const filter = {
      ...apiFilters,
      page: 1,
    };

    dispatch(filterAllServices(filter)).then(() => setIsLoading(false));
    setCurrentPage(1);
  };

  const [showLoginModal, setShowLoginModal] = useState(false);
  return (
    <>
      <Helmet>
        <title>Event To Bee - Les Prestations</title>
        <meta name="description" content="Découvrez les détails des prestations sur Event To Bee." />
      </Helmet>

      <div className={`nc-SectionGridFilterCard ${className}`} data-nc-id="SectionGridFilterCard">
        {showLoginModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black opacity-50" />
            <div className="z-50 p-6 bg-white rounded-lg dark:bg-neutral-900">
              <div className="flex justify-end mb-4">
                <ButtonSecondary onClick={() => setShowLoginModal(false)}>X</ButtonSecondary>
              </div>
              <LoginModal LoginModal={(e) => setShowLoginModal(e)} />
            </div>
          </div>
        )}

        <div className="mb-8">
          <Heading2
            heading={
              <div className="flex items-center">
                <HiOutlineSparkles className="w-5 h-5 mr-2 text-purple-500 lg:w-5 lg:h-5" />
                <span>Recherche intelligente de prestataires</span>
              </div>
            }
            subHeading="Trouvez les meilleurs professionnels pour votre événement avec nos filtres avancés"
          />
        </div>

        <SmartServiceFilters
          className="mb-8"
          onFiltersChange={handleSmartServiceFiltersChange}
          currentFilters={{}}
        />

        {/* Stats et informations */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {serviceState?.length ? (
                <span>
                  <span className="font-semibold text-purple-600 dark:text-purple-400">{serviceState.length}</span>{" "}
                  prestation{serviceState.length > 1 ? "s" : "(s)"} trouvée{serviceState.length > 1 ? "s" : "(s)"}
                  {(fields || region) && " avec vos critères"}
                </span>
              ) : (
                "Recherche en cours..."
              )}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                <span>
                  Page {currentPage} sur {totalPages}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="mb-16">
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <SkeletonLoader />
              </div>
            </div>
          ) : serviceState?.length ? (
            <motion.div
              className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {serviceState.map((service: Service, index: number) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1, ease: EASING.easeOut }}
                  className="transition-all duration-300 transform hover:scale-105 hover:shadow-xl"
                >
                  <ServiceCard
                    data={service}
                    galleryImgs={service.images}
                    showLoginModal={showLoginModal}
                    setShowLoginModal={setShowLoginModal}
                  />
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: EASING.easeOut }}
                className="flex flex-col items-center text-center max-w-md"
              >
                <div className="w-24 h-24 bg-gradient-to-br from-purple-100 to-blue-100 dark:from-purple-900 dark:to-blue-900 rounded-full flex items-center justify-center mb-6">
                  <HiOutlineSquare3Stack3D className="text-3xl text-purple-500" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                  Aucune prestation trouvée
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                  Essayez de modifier vos critères de recherche ou explorez d'autres spécialités pour découvrir
                  plus de prestataires.
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => handleSmartServiceFiltersChange({})}
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-xl font-semibold transition-all duration-300"
                >
                  Réinitialiser les filtres
                </motion.button>
              </motion.div>
            </div>
          )}
        </div>

        {/* Pagination améliorée */}
        {totalPages > 1 && (
          <div className="flex flex-col items-center justify-center mt-16 mb-10 space-y-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Affichage de {(currentPage - 1) * 10 + 1} à {Math.min(currentPage * 10, serviceState?.length || 0)}{" "}
              sur {serviceState?.length || 0} prestataires
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </div>
        )}

        <div className="relative py-16">
          <Header2 title={"Devenez Prestataire et Proposez Votre Prestations !"} url={"/add-service"} />
        </div>
      </div>
    </>
  );
};

export default SectionGridFieldFilterCard;
